import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import BenchlingConfig from 'BenchlingConfig';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import {MultiDocumentPerformanceMonitorModal} from 'notebook/performance/NotebookPerformanceMonitorModal';
import {MultiDocumentPerformanceMonitorToggle} from 'notebook/performance/NotebookPerformanceMonitorToggle';
import {getUnsubmittedTableNames} from 'notebook/reviews/disableAuthorActions';
import {areReviewLifecyclesEnabledForUser} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import {REVIEW_BUTTON_STYLE_FOR_STATUS} from 'notebook/reviews/ReviewActionButton';
import ReviewBadge, {DefaultBadgeSpec} from 'notebook/reviews/ReviewBadge';
import {ReviewAction, ReviewBadgeStatus} from 'notebook/reviews/ReviewConstants';
import WorksheetAnalyzeButton from 'notebook/worksheets/components/WorksheetAnalyzeButton';
import WorksheetExportButton from 'notebook/worksheets/components/WorksheetExportButton';
import {
  WORKSHEET_STEP_TERMINAL_STATUSES,
  WorksheetStepDocumentSelection,
  WorksheetTaskStatusType,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import WorksheetStatusBadge from 'notebook/worksheets/workspace/WorksheetStatusBadge';
import {procedureTypeToName} from 'performance/processWorksheetsEvents';
import {NOT_SPECIFIED} from 'performance/ProfilerConstants';
import WorksheetMetadataComponent from 'procedures/worksheets/WorksheetMetadataComponent';
import {catchWorksheetTestOrderResultError} from 'sample-tests/worksheets/WorksheetTestOrderResultErrorBanner';
import {WorksheetTestOrderUpdatesOnRetractionBanner} from 'sample-tests/worksheets/WorksheetTestOrderUpdatesOnRetractionBanner';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

import {useValidatedWorksheetContext} from '../WorksheetContext';
import {WorksheetViewQuery_worksheet_WorksheetNode as WorksheetNode} from './__generated/WorksheetViewQuery';
import {worksheetStatusStyles} from './WorksheetStatusButton';
import {extractStepTasksData} from './WorksheetUtils';

export interface WorksheetPageHeaderProps {
  name: string;
  worksheetId: string;
  isWorksheetInReview: boolean;
  onSetWorksheet?: (id: string) => boolean;
  onSetSelectedStep?: (stepSelection: WorksheetStepDocumentSelection) => void;
  selectedStep: WorksheetStepDocumentSelection | null;
  worksheetLastActionText: string | null;
  isWorksheetStatusBadgeDisabled: boolean;
  showWorksheetStatusDropdownButton: boolean;
  worksheetStatusBadgeTooltipText: string | null;
}

export function getWorksheetUnsubmittedTableNames(documentIds: Array<string>): Array<string> {
  const allNames: Array<string> = [];
  for (const docId of documentIds) {
    const note = DocumentStore.getDocument(docId)?.documentVersion?.entryVersion?.note;
    if (note != null) {
      allNames.push(...getUnsubmittedTableNames(note));
    }
  }
  return allNames;
}

function needsStepsToBeCompleted(worksheet: WorksheetNode): boolean {
  if (!BenchlingConfig.WORKSHEET_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES) {
    return false;
  }
  const tasks = worksheet.worksheetStepGroups.flatMap((stepGroup) =>
    stepGroup.worksheetSteps.map((step) => extractStepTasksData(step.tasks))
  );
  return tasks.some(
    (task) => task.status === undefined || !WORKSHEET_STEP_TERMINAL_STATUSES.includes(task.status)
  );
}

export default function WorksheetPageHeader({
  name,
  worksheetId,
  isWorksheetInReview,
  onSetWorksheet = (): boolean => false,
  onSetSelectedStep = noop,
  selectedStep = null,
  worksheetLastActionText,
  isWorksheetStatusBadgeDisabled,
  showWorksheetStatusDropdownButton,
  worksheetStatusBadgeTooltipText,
}: WorksheetPageHeaderProps): JSX.Element {
  const enableReviewLifecycle = areReviewLifecyclesEnabledForUser();
  const {
    worksheetTaskData: {procedureType, status},
    refetch,
    worksheet,
    worksheetViewState,
  } = useValidatedWorksheetContext();

  // Function to be called if the worksheet is in review and the review status changes.
  async function refetchDocument(): Promise<void> {
    if (selectedStep?.document.id) {
      await DocumentActions.refresh(selectedStep.document.id);
    }
  }

  const performanceMetadata = useMemo(
    () => ({
      procedure_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
    }),
    [procedureType]
  );

  const [showNotebookPerformanceModal, setShowNotebookPerformanceModal] = useState(false);

  const closeNotebookPerformanceModal = useCallback(() => {
    setShowNotebookPerformanceModal(false);
    LegacyModalContainer.close(MultiDocumentPerformanceMonitorModal);
  }, []);

  const documentIds = useMemo(
    () =>
      worksheet.worksheetStepGroups.flatMap((group) => group.worksheetSteps.map((step) => step.document.id)),
    [worksheet.worksheetStepGroups]
  );

  const worksheetUnsubmittedTableNames = !BenchlingConfig.WORKSHEET_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES
    ? getWorksheetUnsubmittedTableNames(documentIds)
    : undefined;

  const openNotebookPerformanceModal = useCallback(() => {
    if (!showNotebookPerformanceModal) {
      LegacyModalContainer.open(
        MultiDocumentPerformanceMonitorModal,
        <MultiDocumentPerformanceMonitorModal
          documentIds={documentIds}
          onRequestClose={closeNotebookPerformanceModal}
          worksheetId={worksheetId}
        />
      );
      setShowNotebookPerformanceModal(true);
    } else {
      closeNotebookPerformanceModal();
    }
  }, [documentIds, worksheetId, closeNotebookPerformanceModal, showNotebookPerformanceModal]);

  return (
    <div className={css(styles.headerContainer)}>
      <div
        {...getTestProps({component: 'WorksheetPageHeader', element: 'name'})}
        className={css(styles.worksheetHeader)}
      >
        <WorksheetMetadataComponent worksheetId={worksheetId} worksheetName={name} />
        {worksheetLastActionText && <div className={css(styles.status)}>{worksheetLastActionText}</div>}
      </div>
      <div className={css(styles.rightContainer)}>
        {BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS && (
          <MultiDocumentPerformanceMonitorToggle
            key="notebook-performance-monitor-toggle"
            documentIds={documentIds}
            openModal={openNotebookPerformanceModal}
          />
        )}
        {BenchlingConfig.PROCEDURES_ENABLE_SEND_TO_ANALYSIS && (
          <WorksheetAnalyzeButton
            className={css(styles.buttonHeightOverride)}
            worksheetId={worksheetId}
            worksheetName={name}
            isWorksheetInReview={isWorksheetInReview}
            refetch={refetch}
          />
        )}
        <ExportAuditLogButton
          ids={worksheetViewState !== WorksheetViewStateType.LOADED ? [] : [worksheetId]} // Disable the button while the worksheet is reloading
          buttonSize={ButtonSize.MEDIUM}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          className={css(styles.iconButtonOverride)}
        />
        <WorksheetExportButton id={worksheetId} className={css(styles.iconButtonOverride)} />
        {BenchlingConfig.ENABLE_WORKSHEET_REVIEWS && status && TERMINAL_STATUS_TO_DISPLAY_SPEC[status] ? (
          <ReviewBadge
            {...getTestProps({component: 'ReviewBadge', element: 'button'})}
            reviewableId={worksheetId}
            enableReviewLifecycle={enableReviewLifecycle}
            defaultBadgeSpec={TERMINAL_STATUS_TO_DISPLAY_SPEC[status]}
            onReviewChange={refetchDocument}
            renderAdditionalReviewInfo={(reviewableId: string, reviewAction: ReviewAction) =>
              reviewAction === ReviewAction.RETRACT ? (
                <WorksheetTestOrderUpdatesOnRetractionBanner worksheetId={reviewableId} />
              ) : null
            }
            renderReviewError={catchWorksheetTestOrderResultError}
            getSendForReviewValidationError={(reviewableId: string) => {
              if (needsStepsToBeCompleted(worksheet)) {
                return 'Please update all step statuses before sending for review';
              }
              return null;
            }}
            performanceMetadata={performanceMetadata}
            unsubmittedTableNames={worksheetUnsubmittedTableNames}
          />
        ) : (
          <WorksheetStatusBadge
            {...getTestProps({component: 'WorksheetStatusBadge', element: 'button'})}
            name={name}
            disabled={isWorksheetStatusBadgeDisabled}
            showWorksheetStatusDropdownButton={showWorksheetStatusDropdownButton}
            worksheetStatusBadgeTooltipText={worksheetStatusBadgeTooltipText}
          />
        )}
        <div />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  worksheetHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
  },
  rightContainer: {
    display: 'flex',
    gap: '8px',
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonWrapper: {
    marginRight: '8px',
  },
  status: {
    paddingLeft: '10px',
    paddingBottom: '7px',
    fontSize: 12,
    color: GRAY_SEMIDARK,
    alignSelf: 'flex-end',
  },
  iconButtonOverride: {
    height: 32,
    width: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonHeightOverride: {
    display: 'flex',
    height: 32,
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
});

export const TERMINAL_STATUS_TO_DISPLAY_SPEC: {[key in WorksheetTaskStatusType]?: DefaultBadgeSpec} = {
  [WorksheetTaskStatusType.COMPLETED]: {
    text: 'Completed',
    icon: 'done',
    style: REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.ACCEPTED],
  },
  [WorksheetTaskStatusType.SKIPPED]: {
    text: 'Skipped',
    icon: 'forward-step',
    style: worksheetStatusStyles.skipped,
  },
  [WorksheetTaskStatusType.FAILED]: {
    text: 'Failed',
    icon: 'status-failed',
    style: REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.REJECTED],
  },
};
